import { Color, Spacing } from '@persona-id/design-tokens';
import { SearchIcon } from '@persona-id/design-tokens/icons';
import { useCallback, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import { getSearchMatches, SearchMatch } from 'dashboard/lib/lunrFullTextSearch';
import { BoundingBox, DocumentGeneric, Line } from 'dashboard/models/document';
import MultiSearchInput from 'dashboard/views/DashboardDocumentViewerView/MultiSearchInput';
import Button from 'design-system/components/Button';
import Tooltip from 'design-system/components/Tooltip';
import defaultTheme from 'design-system/theme';
import useToggle from 'lib/hooks/useToggle';

interface DocumentViewerSearchProps {
  document: DocumentGeneric;
  inSearchMode: boolean;
  setInSearchMode: (inSearchMode: boolean) => void;
  searchMatches: DocumentSearchMatch[];
  setSearchHighlights: (searchHighlights: DocumentSearchMatch[]) => void;
  currentSearchIndex: number;
  setCurrentSearchIndex: (currentSearchIndex: number) => void;
  disabled?: boolean;
  disabledTooltipText?: string;
}

export interface DocumentSearchMatch {
  boundingBox: BoundingBox;
  pageNumber: number;
  id: number;
}

const HeaderStyledButton = styled(Button)<{ isShown?: boolean }>`
  background-color: ${({ isShown }) => (isShown ? Color.PrimaryWhite : Color.CoreSlate900)};
  margin: 0px ${Spacing[2]};
  &:disabled {
    background-color: ${Color.CoreSlate600};
  }
  svg path {
    ${({ isShown }) => isShown && `stroke: ${Color.CorePurple700};`}
  }
  // Override Button svg stroke style
  svg.pds-icon {
    path {
      ${({ isShown }) => isShown && `stroke: ${Color.CorePurple700};`}
    }

    path[stroke] {
      ${({ isShown }) => isShown && `stroke: ${Color.CorePurple700};`}
    }
  }
`;

const CAPITALIZE_LETTER_SCALE = 1.00008;
const translateSearchMatchToBoundingBox = (
  searchText: string,
  { startPosition }: SearchMatch,
  line: Line,
) => {
  const substringUpUntilStartPosition = line.text.substring(0, startPosition);
  const substringIncludingSearchMatch = line.text.substring(0, startPosition + searchText.length);
  let textUsedForBoundingBoxEstimation;
  // excess prefix that are in textUsedForBoundingBoxEstimation and are not part of searchText
  let excessPrefix;
  let vertices;

  // Newer processed documents have bounding box information for each word
  if (line.words) {
    // assuming words are split by a space
    const indexOfFirstWordThatContainsSearchMatch =
      substringUpUntilStartPosition.split(' ').length - 1;
    const indexOfLastWordThatContainsSearchMatch =
      substringIncludingSearchMatch.split(' ').length - 1;
    const firstWord = line.words[indexOfFirstWordThatContainsSearchMatch];
    const lastWord = line.words[indexOfLastWordThatContainsSearchMatch];
    // Use left vertice from starting word, right vertice from last word in the line
    // Assumes top and bottom from first word found (same line for all words)
    vertices = {
      left: firstWord.vertices.left,
      right: lastWord.vertices.right,
      top: firstWord.vertices.top,
      bottom: firstWord.vertices.bottom,
    };

    textUsedForBoundingBoxEstimation = line.words
      .slice(indexOfFirstWordThatContainsSearchMatch, indexOfLastWordThatContainsSearchMatch + 1)
      .map((word) => word.text)
      .join(' ');
    // last "word" in substringUpUntilStartPositionWords should have the prefix of the firstWord
    // up until the search position.
    excessPrefix = substringUpUntilStartPosition.split(' ').slice(-1)[0];
  } else {
    // if bounding box information for each word is not available, use the entire line to estimate.
    textUsedForBoundingBoxEstimation = line.text;
    excessPrefix = substringUpUntilStartPosition;
    vertices = line.vertices;
  }
  // Vertices are ordered as left, right, top, bottom
  const { left, right, top, bottom } = vertices;
  const charSize = (right - left) / textUsedForBoundingBoxEstimation.length;

  // calculating start and end of the search token, since textUsedForBoundingBoxEstimation has
  // excess prefix and suffix.
  let estimatedLeft = left + charSize * excessPrefix.length;
  const excessPrefixNumCapitalLetters = (excessPrefix.match(/[A-Z]/g) || []).length;
  estimatedLeft *= Math.pow(CAPITALIZE_LETTER_SCALE, excessPrefixNumCapitalLetters);

  let estimatedEnd = estimatedLeft + charSize * searchText.length;
  const matchedLineText = textUsedForBoundingBoxEstimation.substring(
    excessPrefix.length,
    excessPrefix.length + searchText.length,
  );
  const matchedLineTextNumCapitalLetters = (matchedLineText.match(/[A-Z]/g) || []).length;
  estimatedEnd *= Math.pow(CAPITALIZE_LETTER_SCALE, matchedLineTextNumCapitalLetters);
  const boundingBox = {
    top: top,
    left: estimatedLeft,
    right: estimatedEnd,
    bottom: bottom,
  };

  return boundingBox;
};

const DocumentViewerSearch = ({
  document,
  inSearchMode,
  setInSearchMode,
  searchMatches,
  setSearchHighlights,
  currentSearchIndex,
  setCurrentSearchIndex,
  disabled,
  disabledTooltipText,
}: DocumentViewerSearchProps) => {
  const [searchHovered, { disable: setSearchNotHovered, enable: setSearchHovered }] =
    useToggle(false);
  const [searchText, setSearchText] = useState('');
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'f':
            e.stopPropagation();
            e.preventDefault();
            setInSearchMode(true);
            break;
        }
      } else if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        setInSearchMode(false);
      }
    },
    [setInSearchMode],
  );

  window.addEventListener('keydown', handleKeyDown);

  const handleSearch = (text: string) => {
    const pages = document.attributes.pages || [];
    const textLinesByPage = pages.map((page) => page.lines.map((line) => line.text));
    const trimmedText = text.trim();
    const searchMatches = getSearchMatches(trimmedText, textLinesByPage);
    const searchMatchesWithBoundingBoxes = searchMatches
      .map((searchMatch) => ({
        boundingBox: translateSearchMatchToBoundingBox(
          trimmedText,
          searchMatch,
          pages[searchMatch.pageNumber].lines[searchMatch.lineNumber],
        ),
        pageNumber: searchMatch.pageNumber,
      }))
      .sort(
        (match, otherMatch) =>
          match.pageNumber - otherMatch.pageNumber ||
          match.boundingBox.top - otherMatch.boundingBox.top ||
          match.boundingBox.left - otherMatch.boundingBox.left ||
          match.boundingBox.bottom - otherMatch.boundingBox.bottom ||
          match.boundingBox.right - otherMatch.boundingBox.right,
      )
      .map((match, i) => ({
        ...match,
        // in doc viewer pages are 1-indexed since scanner is 1-indexed
        pageNumber: match.pageNumber + 1,
        // give search matches unique id that will be used to jump between searches
        // id's are in order of search results from the top and left most results to the
        // bottom and right most results.
        id: i,
      }));

    setSearchText(trimmedText);
    setSearchHighlights(searchMatchesWithBoundingBoxes);
    setCurrentSearchIndex(searchMatchesWithBoundingBoxes.length > 0 ? 0 : -1);
  };

  const handleSearchNext = () => {
    if (searchMatches.length > 0) {
      // Iterate between 1 and searchHighlights.length since 0 is reserved for 0 of 0 use case
      const newSearchIndex = (currentSearchIndex + 1) % searchMatches.length;
      setCurrentSearchIndex(newSearchIndex);
    }
  };

  const handleSearchPrevious = () => {
    if (searchMatches.length > 0) {
      // make sure the new search index is positive
      const newSearchIndex =
        (((currentSearchIndex - 1) % searchMatches.length) + searchMatches.length) %
        searchMatches.length;
      setCurrentSearchIndex(newSearchIndex);
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Tooltip
        trigger={
          <HeaderStyledButton
            data-test='document-viewer__header--search-button'
            width='icon'
            variant='primary'
            isShown={inSearchMode}
            id='document-viewer__header__search'
            onClick={() => setInSearchMode(!inSearchMode)}
            onPointerEnter={setSearchHovered}
            onPointerLeave={setSearchNotHovered}
            disabled={!!disabled}
          >
            <SearchIcon />
          </HeaderStyledButton>
        }
        hideArrow
        open={inSearchMode || (disabled && searchHovered)}
        onPointerDownOutside={() => setInSearchMode(false)}
        onEscapeKeyDown={() => setInSearchMode(false)}
      >
        {disabled ? (
          disabledTooltipText
        ) : (
          <MultiSearchInput
            handleInput={handleSearch}
            handleOnNext={handleSearchNext}
            handleOnPrevious={handleSearchPrevious}
            handleOnEscape={() => setInSearchMode(false)}
            currentIndex={currentSearchIndex + 1}
            length={searchMatches.length}
            value={searchText}
            placeholder='Search in document'
          />
        )}
      </Tooltip>
    </ThemeProvider>
  );
};

export default DocumentViewerSearch;
